import IconArrowUp from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowUp.js";
import React, { useState, ReactNode, useId } from "react";
import styled from "styled-components";
import media from "@/styles/mediaQueries";

interface MobileAccordionProps {
  children: ReactNode;
  trigger: ReactNode;
}

const Wrapper = styled.div`
  width: 100%;
`;

const AccordionContent = styled.div<{ $isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${(props) => (props.$isOpen ? "1fr" : "0fr")};
  transition: grid-template-rows 0.3s ease-out;
  overflow: hidden;

  ${(props) =>
    props.$isOpen &&
    `
    animation: enableOverflow 0s 0.3s forwards;
  `}

  @keyframes enableOverflow {
    to {
      overflow: visible;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    overflow: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  }

  ${media.screenSmMin({
    gridTemplateRows: "1fr",
    overflow: "visible",
  })}
`;

const AccordionInner = styled.div`
  min-height: 0;
`;

const TriggerWrapper = styled.button`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
  width: 100%;
  background-color: transparent;
  border: none;
  padding: 0.5rem 0;

  /* On desktop: hide trigger */
  ${media.screenSmMin({
    display: "none",
  })}
`;

const StyledIconArrowUp = styled(IconArrowUp)<{ $isOpen: boolean }>`
  transform: ${(props) => (props.$isOpen ? "rotate(0deg)" : "rotate(180deg)")};
  transition: transform 0.3s ease-out;
  color: ${(props) => props.theme.kallisto.color.icon.static.iconSecondary};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MobileAccordion: React.FC<MobileAccordionProps> = ({
  children,
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const id = useId();
  const triggerId = `mobile-accordion-trigger-${id}`;
  const contentId = `mobile-accordion-content-${id}`;

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Wrapper>
      <TriggerWrapper
        id={triggerId}
        onClick={toggleAccordion}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        {trigger}
        <StyledIconArrowUp $isOpen={isOpen} size={14} aria-hidden="true" />
      </TriggerWrapper>
      <AccordionContent
        $isOpen={isOpen}
        id={contentId}
        aria-labelledby={triggerId}
      >
        <AccordionInner>{children}</AccordionInner>
      </AccordionContent>
    </Wrapper>
  );
};

export default MobileAccordion;
